import React, { useState, useMemo, useEffect } from 'react';
import { useFormik } from 'formik';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import { useCreateOrganizationWithPayment } from '@hooks/api/organizations/useCreateOrganizationWithPayment';
import { usePaymentMethods } from '@hooks/api/subscription/usePaymentMethods';
import { useSubscriptionPlans } from '@hooks/api/subscription/useSubscriptionPlans';
import PaymentMethodSelector from '@pages/register/components/PaymentMethodSelector';
import {
  PaymentMethodList,
  BillingPeriodSelector,
  PlanSelectionCards,
  PriceSummary,
} from '@components/organisms/subscription';
import { newCardSchema, existingCardSchema } from './validationSchema';
import ErrorMessage from './ErrorMessage';
import { Loading } from '@components/atoms/Loading';
import FreeTrialLimitNotice from '@components/organisms/FreeTrialLimitNotice';
import { useFreeTrialOrganizationUsage } from '@hooks/api/subscription/useFreeTrialOrganizationUsage';
import { useCreateOrganizationWithTrial } from '@hooks/api/organizations/useCreateOrganizationWithTrial';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { UserRoles } from '@models/user.model';
import { PaymentMethodType } from '@models/subscription';

interface CreateOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (organizationId: string) => void; // TODO: Add type
  userData?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

const CreateOrganizationModal: React.FC<CreateOrganizationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  userData,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [useExistingCard, setUseExistingCard] = useState(true);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null);

  // Get user role to check if SUPER_ADMIN (they don't have access to payment methods)
  const userRole = useAppSelector((state: RootState) => state.user.role);
  const isSuperAdmin = userRole === UserRoles.SUPER_ADMIN;

  // Card-free path. `forcePaidFlow` is the user's explicit opt-in to billing, either from
  // the "set up billing now" link or from the at-cap notice.
  const [forcePaidFlow, setForcePaidFlow] = useState(false);
  const { data: trialUsage, isLoading: isLoadingTrialUsage } =
    useFreeTrialOrganizationUsage(isOpen && !isSuperAdmin);
  const { mutateAsync: createWithTrial, isPending: isCreatingTrial } =
    useCreateOrganizationWithTrial();

  const trialFormik = useFormik({
    initialValues: { organizationName: '' },
    onSubmit: async (values) => {
      setError(null);
      try {
        await createWithTrial({
          organizationName: values.organizationName.trim(),
        });
      } catch (err: any) {
        setError(
          err?.response?.errors?.[0]?.message ||
            err?.message ||
            'Could not create the organization.',
        );
      }
    },
  });

  const { mutateAsync, isPending } = useCreateOrganizationWithPayment();
  // Don't fetch payment methods for SUPER_ADMIN as they don't have access to this endpoint
  const { data: paymentMethods, isLoading: isLoadingPaymentMethods } =
    usePaymentMethods({ enabled: !isSuperAdmin });
  const { data: plans, isLoading: isLoadingPlans } = useSubscriptionPlans();

  // Filter public plans (not addons)
  const publicPlans = useMemo(() => {
    if (!plans) return [];
    return plans.filter((plan) => plan.isPublic && !plan.isAddOn);
  }, [plans]);

  // Get all existing payment methods
  const existingPaymentMethods = useMemo(() => {
    return paymentMethods || [];
  }, [paymentMethods]);

  // Check if there are any existing payment methods
  const hasExistingPaymentMethods = existingPaymentMethods.length > 0;

  // Auto-select the default payment method when available
  useEffect(() => {
    if (hasExistingPaymentMethods && !isLoadingPaymentMethods) {
      setUseExistingCard(true);
      const defaultMethod =
        existingPaymentMethods.find((pm) => pm.isDefault) ||
        existingPaymentMethods[0];
      if (defaultMethod) {
        setSelectedPaymentMethodId(defaultMethod.paymentMethodId);
      }
    }
  }, [
    hasExistingPaymentMethods,
    existingPaymentMethods,
    isLoadingPaymentMethods,
  ]);

  // Use the appropriate validation schema based on selection
  const validationSchema = useMemo(() => {
    return useExistingCard && hasExistingPaymentMethods
      ? existingCardSchema
      : newCardSchema;
  }, [useExistingCard, hasExistingPaymentMethods]);

  const formik = useFormik({
    initialValues: {
      organizationName: '',
      paymentMethodType: PaymentMethodType.CREDIT_CARD,
      billingPeriod: 'MONTHLY',
      selectedPlanId: '',
      // Billing info for new card
      billingFirstName: userData?.firstName || '',
      billingLastName: userData?.lastName || '',
      billingEmail: userData?.email || '',
      billingAddress1: '',
      billingAddress2: '',
      billingCity: '',
      billingState: '',
      billingZip: '',
      billingCountry: 'US',
      billingPhone: '',
      ccNumber: '',
      ccExp: '',
      cvv: '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setError(null);

      try {
        let input: any;

        if (useExistingCard && selectedPaymentMethodId) {
          // Use selected existing payment method
          input = {
            organizationName: values.organizationName,
            billingCycle: values.billingPeriod,
            planId: values.selectedPlanId || publicPlans[0]?.id,
            existingPaymentMethodId: selectedPaymentMethodId,
          };
        } else {
          // Create new payment method.
          // NOTE on `paymentMethodType`: NestJS `registerEnumType` exposes the TS
          // enum KEY as the GraphQL wire format (`CREDIT_CARD`), not the value
          // (`credit_card`). We keep the lowercase value in form state so the UI
          // conditional in PaymentMethodSelector keeps working, and convert to the
          // GraphQL enum name here at the edge.
          input = {
            organizationName: values.organizationName,
            paymentMethodType: 'CREDIT_CARD',
            billingCycle: values.billingPeriod,
            planId: values.selectedPlanId || publicPlans[0]?.id,
            firstName: values.billingFirstName,
            lastName: values.billingLastName,
            email: values.billingEmail,
            address1: values.billingAddress1,
            address2: values.billingAddress2 || undefined,
            city: values.billingCity,
            state: values.billingState,
            zip: values.billingZip,
            country: values.billingCountry || 'US',
            phone: values.billingPhone || undefined,
            ccNumber: values.ccNumber?.replace(/\s/g, ''),
            ccExp: values.ccExp?.replace(/\//g, ''),
            cvv: values.cvv,
          };
        }

        const result = await mutateAsync(input);

        if (result?.organization?.organizationId) {
          onSuccess?.(result?.organization?.organizationId);
          formik.resetForm();
          onClose();
        }
      } catch (err: any) {
        setError(
          err?.response?.errors?.[0]?.message ||
            err?.message ||
            'Failed to create organization. Please try again.',
        );
      }
    },
  });
  // `formik` is rebuilt every render, but setFieldValue is not: Formik wraps it in
  // its own useEventCallback, so it is stable and safe to name as a dependency.
  const { setFieldValue } = formik;

  // Auto-select first public plan if none selected
  useEffect(() => {
    if (publicPlans.length > 0 && !formik.values.selectedPlanId) {
      setFieldValue('selectedPlanId', publicPlans[0].id, false);
    }
  }, [publicPlans, setFieldValue, formik.values.selectedPlanId]);

  // Get selected plan
  const selectedPlan = useMemo(() => {
    if (!publicPlans.length) return null;
    if (formik.values.selectedPlanId) {
      return (
        publicPlans.find((p) => p.id === formik.values.selectedPlanId) ||
        publicPlans[0]
      );
    }
    return publicPlans[0];
  }, [publicPlans, formik.values.selectedPlanId]);

  // Calculate pricing
  const isAnnual = formik.values.billingPeriod === 'ANNUAL';
  const monthlyCost = selectedPlan ? selectedPlan.basePriceCents / 100 : 0;
  const displayPrice = isAnnual ? monthlyCost * 12 : monthlyCost;

  const handleClose = () => {
    if (!isPending) {
      setError(null);
      formik.resetForm();
      trialFormik.resetForm();
      setUseExistingCard(true);
      setSelectedPaymentMethodId(null);
      // Reopening the modal must land on the card-free form again, not on whatever the
      // user opted into last time.
      setForcePaidFlow(false);
      onClose();
    }
  };

  const handleSelectPaymentMethod = (id: string) => {
    setSelectedPaymentMethodId(id);
    setUseExistingCard(true);
  };

  const handleAddNewCard = () => {
    setUseExistingCard(false);
    setSelectedPaymentMethodId(null);
  };

  const isFormValid =
    useExistingCard && hasExistingPaymentMethods
      ? formik.values.organizationName.length >= 3 &&
        selectedPaymentMethodId !== null
      : formik.isValid &&
        formik.values.organizationName &&
        formik.values.ccNumber;

  // Card-free creation is the default for a user with slots left; the paid form is only
  // built once they either run out of slots or choose to pay. SUPER_ADMIN is excluded
  // because the cap is about self-service signups, not administrative creation.
  const showTrialOption = !isSuperAdmin && !forcePaidFlow;
  const atFreeTrialLimit = trialUsage ? !trialUsage.canCreateMore : false;

  if (showTrialOption) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Create New Organization"
        description={
          atFreeTrialLimit
            ? 'Free trial limit reached'
            : 'Start a new organization on a free trial'
        }
        size="large"
        cancelText="Cancel"
        confirmText="Create Organization"
        // Undefined at the cap so Modal drops its footer entirely — FreeTrialLimitNotice
        // carries its own actions, and a second Cancel below them reads as a bug.
        handleConfirm={
          atFreeTrialLimit ? undefined : () => trialFormik.submitForm()
        }
        isLoading={isCreatingTrial}
        disableConfirm={
          atFreeTrialLimit ||
          isCreatingTrial ||
          trialFormik.values.organizationName.trim().length < 2
        }
        closeOnOverlayClick={false}
        closeOnEscape={!isCreatingTrial}>
        {isLoadingTrialUsage ? (
          <div className="py-8">
            <Loading size="lg" center />
          </div>
        ) : atFreeTrialLimit && trialUsage ? (
          <FreeTrialLimitNotice
            used={trialUsage.used}
            limit={trialUsage.limit}
            onCancel={handleClose}
            onContinueWithCard={() => setForcePaidFlow(true)}
          />
        ) : (
          <div className="space-y-6">
            {error && <ErrorMessage message={error} />}

            <FormGroup label="Organization Name" htmlFor="trialOrganizationName">
              <Input
                id="trialOrganizationName"
                name="organizationName"
                value={trialFormik.values.organizationName}
                onChange={trialFormik.handleChange}
                onBlur={trialFormik.handleBlur}
                placeholder="Acme Construction LLC"
              />
            </FormGroup>

            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800/40 dark:bg-blue-900/20">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                No credit card required. This organization starts on a free
                trial — we'll ask for payment details when it ends.
                {trialUsage && (
                  <>
                    {' '}
                    You'll have {trialUsage.used + 1} of {trialUsage.limit} free
                    trials in use.
                  </>
                )}
              </p>
            </div>

            <button
              type="button"
              onClick={() => setForcePaidFlow(true)}
              className="text-sm text-indigo-600 hover:underline dark:text-indigo-400">
              Set up billing now instead
            </button>
          </div>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create New Organization"
      description="Set up a new organization with subscription"
      size="large"
      cancelText="Cancel"
      confirmText="Create Organization"
      handleConfirm={formik.submitForm}
      isLoading={isPending}
      disableConfirm={!isFormValid || isPending}
      closeOnOverlayClick={false}
      closeOnEscape={!isPending}>
      <div className="space-y-6">
        {/* Error Message */}
        {error && <ErrorMessage message={error} />}

        {/* Way back to the card-free form. Opting into billing was a one-way door until
            now: the only escape was closing the modal entirely. Hidden at the cap, where
            the paid flow is the only option left. */}
        {!isSuperAdmin && trialUsage?.canCreateMore && (
          <button
            type="button"
            onClick={() => {
              setError(null);
              setForcePaidFlow(false);
            }}
            className="text-sm text-indigo-600 hover:underline dark:text-indigo-400">
            ← Start a free trial instead
          </button>
        )}

        {/* Organization Name */}
        <FormGroup label="Organization Name" htmlFor="organizationName">
          <Input
            id="organizationName"
            name="organizationName"
            type="text"
            placeholder="Enter organization name"
            value={formik.values.organizationName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(
              formik.touched.organizationName && formik.errors.organizationName,
            )}
          />
          {formik.touched.organizationName &&
            formik.errors.organizationName && (
              <p className="text-sm text-red-500 mt-1">
                {formik.errors.organizationName}
              </p>
            )}
        </FormGroup>

        {/* Payment Method Selection */}
        {hasExistingPaymentMethods && (
          <PaymentMethodList
            paymentMethods={existingPaymentMethods}
            selectedPaymentMethodId={selectedPaymentMethodId}
            onSelectPaymentMethod={handleSelectPaymentMethod}
            showAddNewOption={true}
            isAddingNew={!useExistingCard}
            onAddNew={handleAddNewCard}
            isLoading={isLoadingPaymentMethods}
            disabled={isPending}
          />
        )}

        {/* Show PaymentMethodSelector when adding new card or no existing cards */}
        {(!hasExistingPaymentMethods || !useExistingCard) && (
          <PaymentMethodSelector
            formik={formik}
            userData={userData}
            defaultBillingCycle="monthly"
          />
        )}

        {/* Show Billing Period, Plan Selection, and Price Summary when using existing card */}
        {hasExistingPaymentMethods && useExistingCard && (
          <>
            <BillingPeriodSelector
              selectedPeriod={
                formik.values.billingPeriod as 'MONTHLY' | 'ANNUAL'
              }
              onPeriodChange={(period) =>
                formik.setFieldValue('billingPeriod', period)
              }
              monthlyCost={monthlyCost}
              showPricing={true}
              disabled={isPending}
            />

            <PlanSelectionCards
              plans={publicPlans}
              selectedPlanId={
                formik.values.selectedPlanId || publicPlans[0]?.id || null
              }
              onSelectPlan={(planId) =>
                formik.setFieldValue('selectedPlanId', planId)
              }
              billingPeriod={
                formik.values.billingPeriod as 'MONTHLY' | 'ANNUAL'
              }
              isLoading={isLoadingPlans}
              disabled={isPending}
            />

            {selectedPlan && (
              <PriceSummary
                amount={displayPrice}
                billingPeriod={
                  formik.values.billingPeriod as 'MONTHLY' | 'ANNUAL'
                }
                planName={selectedPlan.name}
              />
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default CreateOrganizationModal;
